import * as React from "react";
import { Home, CategoriesManager, TemplatesManager } from "../components";
import { Route, Routes, useNavigate, useLocation } from "react-router-dom";
import { PostPage } from "./Posts";
import { NewPostPage } from "./NewPostPage";
import { EditPostPage } from "./EditPostPage";
import { GroupCategoryPages } from "./GroupCategoryPages";

const App: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  const navigationItems = [
    { path: "/", label: "Home", icon: "🏠" },
    { path: "/categories", label: "Kategorie", icon: "📁" },
    { path: "/group-categories", label: "Grupy Kategorii", icon: "📂" },
    { path: "/templates", label: "Harmonogramy", icon: "📋" },
    { path: "/post", label: "Posty", icon: "📝" },
  ];

  const isActivePath = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 rounded-md bg-white shadow-md text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-40 w-64 h-screen transition-transform ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 bg-white border-r border-gray-200 shadow-lg`}
      >
        <div className="h-full px-3 py-4 overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8 px-3">
            <h1 className="text-xl font-bold text-gray-900">Admin Panel</h1>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1 rounded-md text-gray-400 hover:text-gray-600"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Navigation */}
          <nav className="space-y-2">
            {navigationItems.map((item) => (
              <button
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isActivePath(item.path)
                    ? "bg-blue-100 text-blue-700"
                    : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                }`}
              >
                <span className="mr-3 text-lg">{item.icon}</span>
                {item.label}
              </button>
            ))}
          </nav>

          {/* Language Selector */}
          <div className="mt-8 pt-4 border-t border-gray-200"></div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="lg:ml-64 min-h-screen">
        <div className="p-4 lg:p-8 pt-16 lg:pt-8">
          <div className="bg-white rounded-lg shadow-sm min-h-[calc(100vh-8rem)]">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/categories" element={<CategoriesManager />} />
              <Route
                path="/group-categories"
                element={<GroupCategoryPages />}
              />
              <Route path="/templates" element={<TemplatesManager />} />
              <Route path="/post" element={<PostPage />} />
              <Route path="/post/new" element={<NewPostPage />} />
              <Route path="/post/edit/:id" element={<EditPostPage />} />
            </Routes>
          </div>
        </div>
      </main>
    </div>
  );
};

export default App;
